import React, { FC, useEffect, useState } from 'react'
import { QueryStatus } from '@reduxjs/toolkit/query'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import { Field, Form, Formik } from 'formik'

import Dialog from '../../styleguide/dialog/Dialog'
import Button from '../../styleguide/buttons/Button'
import { useTeloDispatch, useTeloSelector } from '../../store'
import { selectUsername } from '../../features/auth/selectors'
import Input from '../../styleguide/forms/Input'
import { useCheckInactivityLoginMutation } from '../../services/inactivityLogin'
import CircularProgress from '../../styleguide/CircularProgress'

import { useTeloRouter } from '../../routing/teloRouter'
import authActions from '../../features/auth/actions'
import {
	useGetSessionLockStatusQuery,
	useLockSessionMutation,
	useUnlockSessionMutation,
} from '../../services/sessionLock'

const InactivityModalRow = styled(Box)`
	display: flex;
	flex-direction: row;
	justify-content: center;
	padding-top: 20px;
	flex-wrap: wrap;
	row-gap: 10px;
`

export interface InactivityModalProps {
	time: number
}

const inactivityWorker = new Worker(
	`/webWorkers/inactivityWorker.js?${Math.random()}`,
)

export const InactivityModal: FC<InactivityModalProps> = ({ time }) => {
	const [showModal, setShowModal] = useState<boolean>(false)
	const [isWrongPassword, setIsWrongPassword] = useState<boolean>(false)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const { navigate } = useTeloRouter()
	const dispatch = useTeloDispatch()

	const username = useTeloSelector(selectUsername)

	const [checkLoginInfo, { status, error }] = useCheckInactivityLoginMutation()

	const { data: lockStatus } = useGetSessionLockStatusQuery()
	const [lockSession] = useLockSessionMutation()
	const [unlockSession] = useUnlockSessionMutation()
	useEffect(() => {
		if (lockStatus?.locked) {
			setShowModal(true)
		} else {
			inactivityWorker.postMessage({ action: 'startTimer', time })
		}
	}, [lockStatus, time])

	useEffect(() => {
		inactivityWorker.onmessage = () => {
			lockSession()
			setShowModal(true)

			window.removeEventListener('mousemove', handleUserActivity)
			window.removeEventListener('keypress', handleUserActivity)
			window.onbeforeunload = null
		}

		const handleUserActivity = () => {
			inactivityWorker.postMessage({ action: 'userActivity' })
		}

		window.addEventListener('mousemove', handleUserActivity)
		window.addEventListener('keypress', handleUserActivity)

		if (status === QueryStatus.pending) {
			return setIsLoading(true)
		}

		if (status === QueryStatus.rejected) {
			if (error && (error as any)?.data?.message === 'Token expired') {
				dispatch(authActions.logoutAction())
				navigate('/login')
				window.location.reload()
			}
			setIsLoading(false)
			return setIsWrongPassword(true)
		}

		if (status === QueryStatus.fulfilled) {
			setIsLoading(false)
			setShowModal(false)
			unlockSession()

			return inactivityWorker.postMessage({ action: 'startTimer', time })
		}
	}, [time, status, error, dispatch, navigate, lockSession, unlockSession])

	const onSubmit = async (values: { password: string }) => {
		if (isWrongPassword) {
			setIsWrongPassword(false)
		}

		const password = values.password

		await checkLoginInfo({ username, password })
	}

	return (
		<Dialog open={showModal}>
			<DialogTitle>
				<p>{`Hello ${username}, please insert your password`}</p>
			</DialogTitle>
			<DialogContent>
				<Formik
					initialValues={{ username: username, password: '' }}
					enableReinitialize
					onSubmit={onSubmit}
				>
					<Form>
						{isWrongPassword && (
							<InactivityModalRow>
								<p>
									<Box component="p" color="error.main">
										Incorrect password, please try again
									</Box>
								</p>
							</InactivityModalRow>
						)}
						{isLoading && (
							<InactivityModalRow>
								<Box display="flex" alignSelf="center">
									<CircularProgress />
								</Box>
							</InactivityModalRow>
						)}
						<InactivityModalRow>
							<p>{username}</p>
						</InactivityModalRow>
						<InactivityModalRow>
							<Field
								component={Input}
								name="password"
								label="password"
								fullWidth
								required
								type="password"
							/>
							<Button type="submit">Submit</Button>
						</InactivityModalRow>
					</Form>
				</Formik>
			</DialogContent>
		</Dialog>
	)
}

export default InactivityModal
